/**
 * The cast SENDER side of this app: the scene a sender shows for its receivers to mirror (SenderScene —
 * every sender surface), and the DESKTOP sender's native cast — the state it hands the Rust side (levels,
 * scene) and the "Play on…" push picker. Rust owns the frames and their delivery; every call here is a
 * no-op off the desktop.
 */
import { isNativeAudio } from '../audio/desktop'
import { setCastTarget, type TvOfferLists } from './castTarget'

/** The DESKTOP sender: hand the player's reactivity to the native cast (both the LAN broadcast and the
 *  pushed TV announce it with the entitlement — the Rust sender-state frame). No-op elsewhere. */
export function setCastReactivity(g: number): void {
  nativeCastState('audio_cast_set_reactivity', { reactivity: g })
}

/** The DESKTOP sender: the player's Brightness (EV) for its cast receivers (castProtocol 1.3). No-op elsewhere. */
export function setCastExposure(ev: number): void {
  nativeCastState('audio_cast_set_exposure', { exposure: ev })
}

/** The DESKTOP sender: the player's "calm flashes" switch for its cast receivers (castProtocol 1.4). No-op elsewhere. */
export function setCastCalm(on: boolean): void {
  nativeCastState('audio_cast_set_calm', { calm: on })
}

/** What a SENDER is showing, for its receivers to mirror (castProtocol 1.4 CastScene minus the envelope:
 *  the transport stamps k/v and the per-session `seq`, and re-bases `atSample` onto each receiver's stream). */
export interface SenderScene {
  /** the world on screen — whoever chose it, the sender's Director included — or null when the sender has
   *  no scene (nothing selected): the receiver runs its own Director then */
  world: string | null
  fxMode: 'drive' | 'follow' | null
  /** the world the sender expects next (a receiver may prewarm it) */
  prewarm: string | null
  /** this page's playout sample on ITS OWN cast socket when the cut landed (castPlayoutSample) */
  atSample: number | null
}

/** The DESKTOP sender: hand the scene to the native cast (pull receivers + the pushed TV). No-op elsewhere. */
export function setCastScene(scene: SenderScene): void {
  nativeCastState('audio_cast_set_scene', { world: scene.world, fxMode: scene.fxMode, prewarm: scene.prewarm, atSample: scene.atSample })
}

/** Hand one piece of sender state to the desktop shell's cast (Rust owns the frames + their delivery). */
function nativeCastState(cmd: string, args: Record<string, unknown>): void {
  if (!isNativeAudio()) return
  void import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke(cmd, args))
    .catch(() => { /* command absent (older shell) — ignore */ })
}

// ── the "Play on…" PUSH cast (desktop): browse for TV receivers and push to the picked one ────────
// Deterministic (mirrors mobile/iOS): nothing casts until the user picks a TV. The Rust side browses
// `_hzp-tv._tcp`, pushes the capture's wire frames, and every command returns immediately — the picker
// polls tvcastDevices/tvcastStatus.
export interface TvDevice { name: string; host: string; port: number }
/** The route's status (tvcast.rs). `offer` = what the pushed TV said it can't show — null until it says, and
 *  always null off a live cast (the Rust side clears it with the route). `broadcast` = the TVs pulling this
 *  computer's "Broadcast mode (older TVs)" cast — their names and what any of them can't show (each TV's entry
 *  lives exactly as long as its connection, audio.rs BROADCAST_TVS); null when none is connected. */
export interface TvCastStatus {
  state: 'idle' | 'connecting' | 'connected'; name: string | null; error: string | null; offer?: TvOfferLists | null
  broadcast?: { names: string[]; offer: TvOfferLists } | null
}

async function tvInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T | null> {
  if (!isNativeAudio()) return null
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return await invoke<T>(cmd, args)
  } catch (err) {
    console.warn(`[syqel] ${cmd} failed`, err)
    return null
  }
}

export async function tvcastBrowseStart(): Promise<void> { await tvInvoke('tvcast_browse_start') }
export async function tvcastBrowseStop(): Promise<void> { await tvInvoke('tvcast_browse_stop') }
export async function tvcastDevices(): Promise<TvDevice[]> { return (await tvInvoke<TvDevice[]>('tvcast_devices')) ?? [] }
/** Read the route's status — and with it, refresh the page's mirror of the pushed TV's offer (castTarget.ts). */
export async function tvcastStatus(): Promise<TvCastStatus> {
  const s = (await tvInvoke<TvCastStatus>('tvcast_status')) ?? { state: 'idle', name: null, error: null }
  // the TV(s) this computer casts to: the pushed TV while that route is live, else the broadcast's pulling TVs
  if (s.state === 'connected') setCastTarget(s.name, s.offer ?? null)
  else if (s.broadcast?.names.length) setCastTarget(s.broadcast.names.join(' & '), s.broadcast.offer)
  else setCastTarget(null, null)
  return s
}

// THE PAGE'S ONE STATUS POLL (desktop): every view that needs the route — the "Play on…" picker, the gallery's
// "not available on <TV>" marks — watches this one loop instead of polling on its own, and every read refreshes
// castTarget.ts. Fast (1 s) while a cast is connecting/live or a watcher asks (the picker, open); else 4 s.
interface StatusWatcher { cb: (s: TvCastStatus) => void; fast: () => boolean }
const watchers = new Set<StatusWatcher>()
let pollTimer: number | null = null
let lastState: TvCastStatus['state'] = 'idle'
function pollSoon(now = false): void {
  if (pollTimer !== null || watchers.size === 0) return
  const fast = lastState !== 'idle' || [...watchers].some((w) => w.fast())
  pollTimer = window.setTimeout(() => {
    void tvcastStatus().then((s) => {
      pollTimer = null
      lastState = s.state
      for (const w of [...watchers]) { try { w.cb(s) } catch { /* a watcher must not stop the poll */ } }
      pollSoon()
    })
  }, now ? 0 : fast ? 1000 : 4000)
}

/** Read the status now (a watcher's cadence just changed — the picker opened, a connect was asked for). */
export function pokeCastStatus(): void {
  if (pollTimer !== null) { window.clearTimeout(pollTimer); pollTimer = null }
  pollSoon(true)
}

/** Watch the route's status (desktop; a no-op elsewhere). `fast` asks for the 1 s cadence (e.g. while a picker is
 *  open). Returns the unsubscribe; the poll stops with the last watcher. */
export function watchCastStatus(cb: (s: TvCastStatus) => void, fast: () => boolean = () => false): () => void {
  if (!isNativeAudio()) return () => {}
  const w: StatusWatcher = { cb, fast }
  watchers.add(w)
  pollSoon(true)
  return () => {
    watchers.delete(w)
    if (watchers.size === 0 && pollTimer !== null) { window.clearTimeout(pollTimer); pollTimer = null }
  }
}
export async function tvcastDisconnect(): Promise<void> { await tvInvoke('tvcast_disconnect') }
export async function tvcastConnect(device: TvDevice): Promise<void> {
  await tvInvoke('tvcast_connect', { name: device.name, host: device.host, port: device.port })
}
